import { ItemBadge } from '@liveauctioneers/hammer-ui-core/badge/ItemBadge';
import { createImage } from '@liveauctioneers/hammer-ui-core/card/components/createImage';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';

import CardImage from '@/components/GridItemCard/Card/CardOptions/CategorySearchCards/components/CardImage/CardImage';
import TimedBadgeContainer from '@/components/GridItemCard/Card/CardOptions/TimedPlusItemCard/components/TimedBadgeContainer/TimedBadgeContainer';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import useIsWiderThan from '@/hooks/useIsWiderThan';
import { ItemSummary } from '@/types/item/ItemSummary';
import { ItemCardNavigationTrigger } from '@/utils/amplitude/analytics';
import { useItemCardNavigation } from '@/utils/amplitude/experiments/hooks/useItemCardNavigation';

const MOBILE_IMAGE_QUALITY = 70;
const DESKTOP_IMAGE_QUALITY = 80;

const MOBILE_DIMENSION = 280;
const DESKTOP_DIMENSION = 380;

type ImageComponentProps = ItemSummary & {
    hasNewSimilarItemsIconCTA?: boolean;
    position: number;
};

export const ImageComponent = createImage<ImageComponentProps>({
    ImageComponent: ({
        className,
        hasNewSimilarItemsIconCTA = false,
        isTimedPlusAuction,
        itemId,
        position,
        promoted,
    }) => {
        const { onNavigateInitiated } = useItemCardNavigation(itemId);
        const isMobile = useIsMobileLayout();
        // 1240 = caterwaul's old theme.breakpoints.pageWidthNum — no hammer-ui token matches exactly.
        const useSmallerImageQuality = !useIsWiderThan(1240);

        const handleMouseHover = (isHovered: boolean) => {
            const itemCard = document.getElementById(`card-${itemId}`);
            if (itemCard) {
                if (isHovered) {
                    itemCard.classList.add('image-hover');
                } else {
                    itemCard.classList.remove('image-hover');
                }
            }
        };

        return (
            <>
                {isTimedPlusAuction && <TimedBadgeContainer itemId={itemId} />}
                {promoted && !isTimedPlusAuction && (
                    <ItemBadge
                        className="absolute left-xs top-xs z-[1]"
                        isFeatured
                    />
                )}
                <div
                    className={cn(
                        'overflow-hidden border-border-tertiary',
                        hasNewSimilarItemsIconCTA ? 'border-b smMin:!mb-2xs' : 'rounded-md border',
                        className
                    )}
                >
                    <CardImage
                        applyDimensionToUrl
                        canShowEasyShipping
                        dimension={useSmallerImageQuality ? MOBILE_DIMENSION : DESKTOP_DIMENSION}
                        enterLinkHoverState={() => handleMouseHover(true)}
                        imageQuality={useSmallerImageQuality ? MOBILE_IMAGE_QUALITY : DESKTOP_IMAGE_QUALITY}
                        isLabeledFavoriteButton
                        isMobile={isMobile}
                        itemId={itemId}
                        lazyLoadImage
                        leaveLinkHoverState={() => handleMouseHover(false)}
                        onNavigate={() => {
                            onNavigateInitiated(ItemCardNavigationTrigger.ImageClick, { position, promoted });
                        }}
                        position={position}
                        sharpenImage
                        shouldShowSeeSimilarItemsButtonIconCTA={hasNewSimilarItemsIconCTA}
                    />
                </div>
            </>
        );
    },
});
